import React, { useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import api from "@/lib/api";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useMasterList, useInvalidateMaster } from "@/hooks/useMasterData";

/**
 * Multi-select dropdown bound to a master-data list. Supports:
 * - parentIds[] for cascading (fetches union of children across all parents)
 * - allowAdd inline (with auto-approval flag, e.g. for tags)
 * - shows chip summary outside the trigger and X-removal on each chip
 */
export default function MasterDataMultiSelect({
  listType,
  parentIds = null,
  values = [],
  onChange,
  placeholder = "Select…",
  allowAdd = false,
  disabled = false,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  // No-parent mode: fetch once.
  const singleList = useMasterList(listType, { parentId: parentIds === null ? undefined : null });

  // Cascading mode: fetch children for each parent.
  const parentQueries = useQueries({
    queries: (parentIds || []).map((pid) => ({
      queryKey: ["master", listType, pid, false],
      queryFn: async () => (await api.get(`/master-data/${listType}`, { params: { parent_id: pid } })).data,
      staleTime: 30_000,
    })),
  });

  const options = useMemo(() => {
    if (parentIds === null) return singleList.data || [];
    const all = parentQueries.flatMap((q) => q.data || []);
    // dedupe by id
    const seen = new Set();
    return all.filter((o) => (seen.has(o.id) ? false : (seen.add(o.id), true)));
  }, [parentIds, singleList.data, parentQueries]);

  const selected = useMemo(() => options.filter((o) => values.includes(o.id)), [options, values]);

  const filtered = useMemo(() => {
    if (!query) return options;
    return options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()));
  }, [options, query]);

  const exactMatch = useMemo(
    () => filtered.find((o) => o.label.toLowerCase() === query.trim().toLowerCase()),
    [filtered, query]
  );

  const invalidate = useInvalidateMaster();

  const toggle = (id) => {
    const set = new Set(values);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange?.(Array.from(set));
  };

  const removeChip = (id) => onChange?.(values.filter((v) => v !== id));

  const handleAdd = async () => {
    const label = query.trim();
    if (!label) return;
    try {
      const { data } = await api.post(`/master-data/${listType}`, {
        list_type: listType, label, parent_id: null,
      });
      const msg = data.review_status === "approved" ? `"${data.label}" added` : `"${data.label}" added (pending review)`;
      toast.success(msg);
      invalidate(listType);
      toggle(data.id);
      setQuery("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add value");
    }
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            role="combobox"
            className={cn("w-full justify-between font-normal", selected.length === 0 && "text-slate-400")}
            data-testid={testId || `multi-${listType}`}
          >
            <span className="truncate">
              {selected.length === 0 ? placeholder : `${selected.length} selected`}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search…"
              value={query}
              onValueChange={setQuery}
              data-testid={`${testId || `multi-${listType}`}-search`}
            />
            <CommandList>
              <CommandEmpty>
                {allowAdd && query.trim() ? (
                  <button
                    type="button"
                    onClick={handleAdd}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                    data-testid={`${testId || `multi-${listType}`}-add`}
                  >
                    <Plus className="h-4 w-4 text-[#B8935A]" />
                    <span>Add <strong>"{query.trim()}"</strong> as new</span>
                  </button>
                ) : (
                  <div className="py-6 text-center text-xs text-slate-500">No results</div>
                )}
              </CommandEmpty>
              <CommandGroup>
                {filtered.map((o) => {
                  const checked = values.includes(o.id);
                  return (
                    <CommandItem
                      key={o.id}
                      value={o.label}
                      onSelect={() => toggle(o.id)}
                      data-testid={`${testId || `multi-${listType}`}-option-${o.id}`}
                    >
                      <Check className={cn("mr-2 h-4 w-4", checked ? "opacity-100" : "opacity-0")} />
                      <span className="flex-1">{o.label}</span>
                      {o.review_status === "pending" && (
                        <span className="ml-1.5 text-[10px] uppercase font-semibold text-amber-700">pending</span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              {allowAdd && query.trim() && !exactMatch && (
                <div className="border-t border-slate-200">
                  <button
                    type="button"
                    onClick={handleAdd}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                    data-testid={`${testId || `multi-${listType}`}-add`}
                  >
                    <Plus className="h-4 w-4 text-[#B8935A]" />
                    <span>Add <strong>"{query.trim()}"</strong> as new</span>
                  </button>
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((s) => (
            <span
              key={s.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-slate-100 border border-slate-200 rounded"
              data-testid={`${testId || `multi-${listType}`}-chip-${s.id}`}
            >
              {s.label}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeChip(s.id)}
                  className="text-slate-500 hover:text-slate-900"
                  data-testid={`${testId || `multi-${listType}`}-chip-remove-${s.id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
